import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SubtitleLayerEditor from '@/components/subtitles/SubtitleLayerEditor';
import { normalizeSubtitleLayer } from '@/lib/subtitles/subtitleLayer';

/**
 * CapCut-style draggable title on intro/outro card previews (full-bleed frame).
 */
export default function BookendTitleOverlay({
  containerRef,
  aspectRatio = '16:9',
  titleStyle,
  text = '',
  viewportMin = 900,
  interactive = true,
  snapToGrid = false,
  onLayerChange,
  onTextChange,
}) {
  const [frameSize, setFrameSize] = useState({ w: 0, h: 0 });
  const [interacting, setInteracting] = useState(false);
  const frameScreenRectRef = useRef(null);

  useEffect(() => {
    const el = containerRef?.current;
    if (!el) return undefined;

    const measure = () => {
      const rect = el.getBoundingClientRect();
      const w = Math.round(rect.width);
      const h = Math.round(rect.height);
      if (w > 0 && h > 0) {
        setFrameSize({ w, h });
        frameScreenRectRef.current = {
          left: rect.left,
          top: rect.top,
          width: w,
          height: h,
        };
      }
    };

    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [containerRef]);

  const resolvedLayer = useMemo(() => {
    if (titleStyle?.layer) return normalizeSubtitleLayer(titleStyle.layer, aspectRatio);
    return normalizeSubtitleLayer({ x: 0.5, y: 0.5, width: 0.88, height: 0.22, rotation: 0, textAlign: 'center' }, aspectRatio);
  }, [titleStyle?.layer, aspectRatio]);

  const handleLayerChange = useCallback(
    (nextLayer) => {
      onLayerChange?.(normalizeSubtitleLayer(nextLayer, aspectRatio));
    },
    [aspectRatio, onLayerChange],
  );

  const refreshScreenRect = useCallback(() => {
    const el = containerRef?.current;
    if (!el) return frameScreenRectRef.current;
    const rect = el.getBoundingClientRect();
    const next = {
      left: rect.left,
      top: rect.top,
      width: Math.round(rect.width),
      height: Math.round(rect.height),
    };
    frameScreenRectRef.current = next;
    return next;
  }, [containerRef]);

  if (frameSize.w < 1 || frameSize.h < 1) return null;

  const frameScreenRect = frameScreenRectRef.current || {
    left: 0,
    top: 0,
    width: frameSize.w,
    height: frameSize.h,
  };

  return (
    <div
      className="absolute inset-0 z-20 pointer-events-none"
      data-testid="bookend-title-overlay"
    >
      <div className="absolute inset-0 pointer-events-auto">
        <SubtitleLayerEditor
          layer={resolvedLayer}
          style={titleStyle}
          text={text}
          frameWidth={frameSize.w}
          frameHeight={frameSize.h}
          frameScreenRect={frameScreenRect}
          aspectRatio={aspectRatio}
          viewportMin={viewportMin}
          isActive={!interacting}
          interactive={interactive}
          snapToGrid={snapToGrid}
          onLayerChange={handleLayerChange}
          onTextChange={onTextChange}
          onInteractionStart={() => {
            refreshScreenRect();
            setInteracting(true);
          }}
          onInteractionEnd={() => setInteracting(false)}
        />
      </div>
    </div>
  );
}
